import axios from "axios";
import { gsap } from "gsap";
import { TextPlugin } from "gsap/TextPlugin";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useCallCallie } from "../components/CallCallie";
import GlowBalls from "../components/GlowBalls";
import Layout from "../components/Layout";
import Sectiontitle from "../components/Sectiontitle";
import Spinner from "../components/Spinner";

gsap.registerPlugin(TextPlugin);

const AI_BALLS = [
  { color: "#39ff14", glow: "rgba(57, 255, 20, 0.7)", r: 45 },
  { color: "#00d4ff", glow: "rgba(0, 212, 255, 0.7)", r: 38 },
  { color: "#ff6b6b", glow: "rgba(255, 107, 107, 0.7)", r: 50 },
  { color: "#c77dff", glow: "rgba(199, 125, 255, 0.7)", r: 42 },
  { color: "#ffd166", glow: "rgba(255, 209, 102, 0.7)", r: 35 },
];

function useLightMode() {
  const [isLight, setIsLight] = useState(() => document.body.classList.contains("light"));
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsLight(document.body.classList.contains("light"))
    );
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return isLight;
}

function AIProjectCard({ project, onAction, actionLabel, actionStatus, callActive, isLight }) {
  const isAction = project.url && project.url.startsWith("javascript:");
  return (
    <div className="mi-aiproject-card">
      <span className="mi-aiproject-category">{project.category}</span>
      <h3>{project.title}</h3>
      <p>{project.description}</p>
      {isAction ? (
        <>
          <div className="mi-aiproject-card-bottom">
            <button onClick={onAction} className="mi-button mi-aiproject-btn">
              {actionLabel || "Open"}
            </button>
            <div className={`mi-aiproject-callie-icon${callActive ? " active" : ""}`}>
              <img
                src={isLight ? "/images/callie_for_light.svg" : "/images/callie.svg"}
                alt="Callie"
              />
            </div>
          </div>
          {actionStatus && <p className="mi-aiproject-status">{actionStatus}</p>}
        </>
      ) : project.url ? (
        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mi-button mi-aiproject-btn"
        >
          Open
        </a>
      ) : null}
    </div>
  );
}

function AIProjects() {
  const [projects, setProjects] = useState([]);
  const { toggle: toggleCallie, callActive, status: callieStatus } = useCallCallie();
  const isLight = useLightMode();
  const headingRef = useRef(null);
  const cursorRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    axios.get("/api/aiprojects").then((res) => {
      if (mounted) setProjects(res.data);
    });
    return () => (mounted = false);
  }, []);

  useEffect(() => {
    const heading = "GSAP animations in progress...";

    const tl = gsap.timeline({ delay: 0.3 });
    tl.to(headingRef.current, {
      duration: heading.length * 0.07,
      text: { value: heading },
      ease: "none",
    });

    const cursorBlink = gsap.to(cursorRef.current, {
      opacity: 0,
      duration: 0.5,
      repeat: -1,
      yoyo: true,
      ease: "power1.inOut",
    });

    return () => {
      tl.kill();
      cursorBlink.kill();
    };
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>AI Projects - calee.me</title>
        <meta
          name="description"
          content="AI-powered tools and experiments by Carrie Lee"
        />
      </Helmet>
      <Suspense fallback={<Spinner />}>
        <div className="mi-aiprojects-section mi-section mi-padding-top mi-padding-bottom">
          <div className="container">
            <Sectiontitle title="AI Projects" />
            <div className="mi-aiprojects-hero">
              <GlowBalls configs={AI_BALLS} />
              <div className="mi-aiprojects-hero-content">
                <h1 className="mi-aiprojects-heading">
                  <span ref={headingRef}></span>
                  <span ref={cursorRef} className="mi-aiprojects-cursor">
                    |
                  </span>
                </h1>
              </div>
            </div>
            <div className="mi-aiprojects-grid">
              {projects.map((project) => {
                const isCallie = project.url?.startsWith("javascript:");
                return (
                  <AIProjectCard
                    key={project.id}
                    project={project}
                    onAction={isCallie ? toggleCallie : undefined}
                    actionLabel={isCallie ? (callActive ? "End Call" : "Talk to Callie") : undefined}
                    actionStatus={isCallie ? callieStatus : undefined}
                    callActive={isCallie ? callActive : false}
                    isLight={isLight}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </Suspense>
    </Layout>
  );
}

export default AIProjects;
